import { useEffect, useRef, useState } from "react";
import { useDebouncedAutosave, type AutosaveStatus } from "../../shared/useDebouncedAutosave";
import { downloadCustomFile, fetchExportTemplates, saveCustomDoc, type ExportTemplate } from "./api";

interface Props {
  slug: string | null;
  /** Latest known content from disk — re-seeded by file_changed. */
  remoteContent: string;
  exportTemplate: string;
  onTemplateChange: (id: string) => void;
  onNew: () => void;
}

export function Step2Edit({ slug, remoteContent, exportTemplate, onTemplateChange, onNew }: Props) {
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState(remoteContent);
  const [templates, setTemplates] = useState<ExportTemplate[]>([{ id: "plain", label: "Standard (ohne Vorlage)" }]);
  const [status, setStatus] = useState<{ kind: AutosaveStatus; message?: string }>({ kind: "idle" });
  const [docxBusy, setDocxBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const list = await fetchExportTemplates();
      setTemplates(list);
      if (!list.find((t) => t.id === exportTemplate)) {
        const fallback = list.find((t) => t.id === "plain")?.id ?? list[0]?.id ?? "plain";
        onTemplateChange(fallback);
      }
    })();
  }, [exportTemplate, onTemplateChange]);

  // Re-seed editor from disk if user is not currently typing.
  useEffect(() => {
    if (document.activeElement === editorRef.current) return;
    if (remoteContent === content) return;
    setContent(remoteContent);
  }, [remoteContent, content]);

  const { schedule, flush } = useDebouncedAutosave({
    save: async () => {
      if (!slug) return;
      const value = editorRef.current?.value ?? content;
      await saveCustomDoc(slug, value);
    },
    onStatus: (kind, message) => {
      setStatus({ kind, message });
      if (kind === "saved") window.setTimeout(() => setStatus({ kind: "idle" }), 2000);
    },
  });

  function statusText(): string {
    if (status.kind === "saving") return "Speichern…";
    if (status.kind === "saved") return "Gespeichert";
    if (status.kind === "error") return "Fehler: " + (status.message ?? "");
    return "";
  }

  async function handleManualSave() {
    await flush();
    if (slug && content === editorRef.current?.value) {
      try {
        setStatus({ kind: "saving" });
        await saveCustomDoc(slug, content);
        setStatus({ kind: "saved" });
        window.setTimeout(() => setStatus({ kind: "idle" }), 2000);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({ kind: "error", message });
      }
    }
  }

  async function handleDocx() {
    if (!slug) return;
    setError(null);
    await flush();
    setDocxBusy(true);
    try {
      const result = await downloadCustomFile(slug, "docx", exportTemplate);
      if (!result.ok) setError(result.error ?? "DOCX-Generierung fehlgeschlagen");
    } finally {
      setDocxBusy(false);
    }
  }

  async function handlePdf() {
    if (!slug) return;
    setError(null);
    await flush();
    setPdfBusy(true);
    try {
      const result = await downloadCustomFile(slug, "pdf", exportTemplate);
      if (!result.ok) setError(result.error ?? "PDF-Generierung fehlgeschlagen");
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <section className="wizard-section" data-custom-step="2">
      <h2 className="form-title">Dokument bearbeiten &amp; exportieren</h2>
      <p className="form-description">
        Bearbeite das Dokument direkt oder nutze den Chat rechts, um Änderungen per KI vorzunehmen.
        Wähle dann eine Exportvorlage und lade das fertige Dokument herunter.
      </p>
      {error && (
        <div className="info-box" role="alert" style={{ marginBottom: "0.75rem" }}>
          {error}{" "}
          <button type="button" className="link-button" onClick={() => setError(null)}>
            Schließen
          </button>
        </div>
      )}
      <div className="form-row form-row-full">
        <label htmlFor="custom-doc-editor">Dokumentinhalt</label>
        <span
          className={`autosave-status${status.kind === "saving" ? " saving" : ""}${status.kind === "saved" ? " saved" : ""}`}
          aria-live="polite"
        >
          {statusText()}
        </span>
        <textarea
          ref={editorRef}
          id="custom-doc-editor"
          rows={16}
          placeholder="Dokument wird geladen…"
          style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            schedule();
          }}
        />
      </div>
      <div className="form-row">
        <label htmlFor="custom-export-template">Exportvorlage</label>
        <select
          id="custom-export-template"
          value={exportTemplate}
          onChange={(e) => onTemplateChange(e.target.value)}
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <span className="field-hint">
          Bestimmt Schriftart, Layout und Formatierung der exportierten Datei.
        </span>
      </div>
      <div className="form-actions" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn-secondary" onClick={() => void handleManualSave()}>
          Speichern
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void handleDocx()}
          disabled={!slug || docxBusy}
        >
          {docxBusy ? "Generiere DOCX…" : "Download DOCX"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void handlePdf()}
          disabled={!slug || pdfBusy}
        >
          {pdfBusy ? "Generiere PDF…" : "Download PDF"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onNew}>
          Neues Dokument
        </button>
      </div>
    </section>
  );
}
